import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

interface PeriodPickerProps {
  selected: string; // ISO month "YYYY-MM"
  onSelect: (month: string) => void;
}

function monthLabel(iso: string): string {
  const [year, month] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function lastNMonths(n: number, anchor = new Date()): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export function PeriodPicker({ selected, onSelect }: PeriodPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const months = lastNMonths(12);
  if (!months.includes(selected)) months.unshift(selected);

  return (
    <div ref={ref} className="relative w-fit">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 bg-white border border-line rounded-lg px-3 py-2 text-[12px] font-semibold text-ink2 hover:bg-[#FAFBFB] transition-colors"
      >
        {monthLabel(selected)}
        <ChevronDown size={13} strokeWidth={2.5} className="text-ink3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 w-56 bg-white border border-line rounded-lg shadow-lg z-50 overflow-hidden py-1 max-h-72 overflow-y-auto">
          {months.map((m) => {
            const isSel = m === selected;
            return (
              <button
                key={m}
                type="button"
                onClick={() => { onSelect(m); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#FAFBFB] text-[12.5px] ${isSel ? "font-semibold text-ink bg-sage" : "text-ink2"}`}
              >
                {monthLabel(m)}
                {isSel && <Check size={14} className="text-teal" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}